import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { AnsweringSubscriptionLevel } from '@bb/domain/dist/types';
import { hasValidStripeMlsProductId } from 'utils';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { Breadcrumbs, Page } from 'components';

import type { GetPortalAnsweringConfigResponse } from '@/app-package';
import { AppointmentDeskContextProvider } from './AppointmentDeskContext';
import AppointmentDeskLayout from './AppointmentDeskLayout';
import { StyledLoading } from './StyledComponents';
import AppointmentDeskError from './AppointmentDeskError';

const STATIC_BREADCRUMBS = [
  { breadcrumbName: 'Admin', path: '/admin' },
  { breadcrumbName: 'Live Concierge Service', path: '/admin/appointment_desk' },
];

interface AppointmentDeskPageProps {
  // Optional portal config passed in from external for agent setup.
  // On brokerage setup, this argument will not be passed
  portalConfig?: GetPortalAnsweringConfigResponse;
  subscriptionType: AnsweringSubscriptionLevel;
  userId?: string;
  billingRedirection: () => void;
  userMlsNumbers?: { boardId?: string }[];
}

const AppointmentDeskPage: React.FC<AppointmentDeskPageProps> = ({
  subscriptionType,
  userId,
  billingRedirection,
  userMlsNumbers,
}) => {
  const {
    auth,
    answeringPortalConfig,
    answeringPortalConfigError,
    loadingAnsweringPortalConfig,
    onboarding,
    hasLimitedAccess,
  } = useContext(AuthContext);
  const history = useHistory();
  const { md } = useBreakpoint();

  const isBoardCovered = useMemo(() => {
    // board only can cover users atm
    if (subscriptionType === 'org') return false;
    return answeringPortalConfig?.appointmentDeskBoardPays
      ?.filter(({ payForUsers }) => payForUsers)
      .some(({ boardId }) =>
        userMlsNumbers?.some(
          ({ boardId: userBoardId }) => boardId === userBoardId,
        ),
      );
  }, [
    answeringPortalConfig?.appointmentDeskBoardPays,
    subscriptionType,
    userMlsNumbers,
  ]);

  let pageContent: JSX.Element | null = null;
  if (loadingAnsweringPortalConfig) {
    pageContent = StyledLoading;
  } else if (answeringPortalConfigError) {
    pageContent = <AppointmentDeskError />;
  } else if (!answeringPortalConfig && subscriptionType === 'user') {
    // TODO: For agent setup, Portal Config off pages are descoped until RECO launch
    return null;
  } else if (
    !answeringPortalConfig?.useLiveAppointmentDesk &&
    !hasValidStripeMlsProductId(answeringPortalConfig?.mlsProductId)
  ) {
    history.replace('/admin/answering');
  } else {
    pageContent = (
      <AppointmentDeskContextProvider
        orgId={auth.org.id}
        brokerageLaunchDate={onboarding?.launchDate}
        subscriptionType={subscriptionType}
        userId={userId}
        billingRedirection={billingRedirection}
        isBoardCovered={isBoardCovered}
      >
        <AppointmentDeskLayout hasLimitedAccess={hasLimitedAccess} />
      </AppointmentDeskContextProvider>
    );
  }

  return subscriptionType === 'org' ? (
    <Page
      overTitle={
        <StyledOverTitle>
          <Breadcrumbs breadcrumbRoutes={STATIC_BREADCRUMBS} />
        </StyledOverTitle>
      }
      title={md ? 'Live Concierge Service' : undefined}
      showBackButton={md}
      goBackUrl="/admin"
      type="hollow"
      extraTitleMargin
    >
      {pageContent}
    </Page>
  ) : (
    pageContent
  );
};

export default AppointmentDeskPage;

const StyledOverTitle = styled.div`
  margin-bottom: 10px;
`;
